/**
 * Navigate after a successful sign-in, honouring a return path only if the
 * user may actually view it.
 *
 * `destination` is `postLoginRedirect({..., returnTo})` and `fallback` is the
 * same call without `returnTo` (the plain role landing). When the router's
 * capability guards deny the deep link they redirect to /login -- for a
 * signed-in user that is a dead end, not a 403 loop, so we fall back to the
 * role landing instead.
 *
 * `deeplink` (optional) is the deep-link auto-impersonation step
 * (router/deeplinkImpersonationRuntime.js). It runs only when a return path
 * is in play (destination !== fallback), BEFORE the SPA navigates, because a
 * seat switch changes the capability set and ends in a hard navigation:
 *   'reloading' -> do nothing (the page is being replaced),
 *   'landing'   -> the role landing instead of the link,
 *   'proceed'   -> the link, as before.
 *
 * @returns {Promise<string|undefined>} the fullPath the router ended on
 */
export async function navigateAfterLogin(router, destination, fallback, deeplink) {
  if (deeplink && destination !== fallback) {
    let outcome = "proceed";
    try {
      outcome = await deeplink(router.resolve(destination));
    } catch (_e) {
      // A failed resolution never strands the user on /login.
      outcome = "landing";
    }
    if (outcome === "reloading") return undefined;
    if (outcome === "landing") destination = fallback;
  }
  try {
    await router.push(destination);
  } catch (_e) {
    // A navigation failure is handled by the check below.
  }
  const here = router.currentRoute?.value;
  if (destination !== fallback && here && here.path === "/login") {
    try {
      await router.push(fallback);
    } catch (_e) {
      // Nothing further to try; the user stays where the router left them.
    }
  }
  return router.currentRoute?.value?.fullPath;
}

export default navigateAfterLogin;
